import { BlockBlobClient } from '@azure/storage-blob';

import { FileUploadDTO } from '@/types/api-types';

const CHUNK_SIZE = 100 * 1024 * 1024; // 100MB chunks
const MAX_CONCURRENT_UPLOADS = 6;
const MAX_RETRY_ATTEMPTS = 3; // Add this constant for max retries

interface UploadProgress {
  uploaded: number;
  total: number;
}

export class FileUploadService {
  private uploadQueue: Map<
    string,
    {
      file: File;
      progress: UploadProgress;
      chunks: Blob[];
      blockIds: string[];
      pause: boolean;
      activeUploads: number;
      completedChunks: Set<string>;
      blobClient: BlockBlobClient;
      retryAttempts: Map<string, number>; // Add retry counter for each block
    }
  > = new Map();

  async uploadFile(
    fileObject: FileUploadDTO,
    onProgress: (progress: UploadProgress) => void,
  ): Promise<void> {
    if (!fileObject.file_data) {
      throw new Error('File data is required');
    }
    const chunks = this.createChunks(fileObject.file_data);
    const blockIds = chunks.map((_, index) =>
      btoa(`block-${index.toString().padStart(6, '0')}`),
    );

    // Create blob client with the SAS URL from backend
    const blobClient = new BlockBlobClient(fileObject.write_url!);

    this.uploadQueue.set(fileObject.uuid, {
      file: fileObject.file_data,
      progress: { uploaded: 0, total: chunks.length },
      chunks,
      blockIds,
      pause: false,
      activeUploads: 0,
      completedChunks: new Set(),
      blobClient,
      retryAttempts: new Map(), // Initialize retry attempts map
    });

    return this.processQueue(fileObject.uuid, onProgress);
  }

  private createChunks(file: File): Blob[] {
    const chunks: Blob[] = [];
    let start = 0;

    while (start < file.size) {
      const end = Math.min(start + CHUNK_SIZE, file.size);
      chunks.push(file.slice(start, end));
      start = end;
    }

    return chunks;
  }

  private async processQueue(
    fileId: string,
    onProgress: (progress: UploadProgress) => void,
  ): Promise<void> {
    const upload = this.uploadQueue.get(fileId);
    if (!upload) return;

    const { chunks, blockIds, completedChunks, blobClient } = upload;

    const uploadChunk = async (index: number) => {
      if (upload.pause || index >= chunks.length) return;

      if (completedChunks.has(blockIds[index])) {
        return;
      }

      upload.activeUploads++;
      const blockId = blockIds[index];
      const attempts = upload.retryAttempts.get(blockId) || 0;

      try {
        if (attempts >= MAX_RETRY_ATTEMPTS) {
          throw new Error(
            `Maximum retry attempts (${MAX_RETRY_ATTEMPTS}) exceeded for chunk ${index}`,
          );
        }

        upload.retryAttempts.set(blockId, attempts + 1);

        await blobClient.stageBlock(
          blockId,
          chunks[index],
          chunks[index].size,
          {
            onProgress: (_) => {},
          },
        );
        this.handleChunkUploaded(fileId, blockId, onProgress);
      } catch (error) {
        if (attempts >= MAX_RETRY_ATTEMPTS - 1) {
          // throw new Error(
          //   `Failed to upload chunk ${index} after ${MAX_RETRY_ATTEMPTS} attempts: ${fileId}`,
          // );
        }
      } finally {
        upload.activeUploads--;
        this.tryUploadNextChunk(fileId, onProgress);
      }
    };

    // Start initial parallel uploads
    const initialUploads = Math.min(MAX_CONCURRENT_UPLOADS, chunks.length);
    const uploadPromises = [];
    for (let i = 0; i < initialUploads; i++) {
      uploadPromises.push(uploadChunk(i));
    }
    await Promise.all(uploadPromises);
  }

  private async tryUploadNextChunk(
    fileId: string,
    onProgress: (progress: UploadProgress) => void,
  ) {
    const upload = this.uploadQueue.get(fileId);
    if (!upload) return;

    const nextChunkIndex = upload.progress.uploaded;

    if (
      nextChunkIndex < upload.chunks.length &&
      !upload.pause &&
      upload.activeUploads < MAX_CONCURRENT_UPLOADS
    ) {
      const { chunks, blockIds, blobClient } = upload;
      const blockId = blockIds[nextChunkIndex];
      const attempts = upload.retryAttempts.get(blockId) || 0;

      upload.activeUploads++;

      try {
        if (attempts >= MAX_RETRY_ATTEMPTS) {
          throw new Error(
            `Maximum retry attempts (${MAX_RETRY_ATTEMPTS}) exceeded for chunk ${nextChunkIndex}`,
          );
        }

        upload.retryAttempts.set(blockId, attempts + 1);

        await blobClient.stageBlock(
          blockId,
          chunks[nextChunkIndex],
          chunks[nextChunkIndex].size,
        );
        this.handleChunkUploaded(fileId, blockId, onProgress);
      } catch (error) {
        if (attempts >= MAX_RETRY_ATTEMPTS - 1) {
        }
      } finally {
        upload.activeUploads--;
        this.tryUploadNextChunk(fileId, onProgress);
      }
    } else if (
      upload.progress.uploaded >= upload.chunks.length &&
      upload.activeUploads === 0
    ) {
      // All chunks uploaded, commit the blocks

      try {
        // Encode filename to handle non-ASCII characters
        const encodedFilename = encodeURIComponent(upload.file.name);

        await upload.blobClient.commitBlockList(upload.blockIds, {
          blobHTTPHeaders: {
            blobContentType: upload.file.type || 'application/octet-stream',
          },
          metadata: {
            filename: encodedFilename,
            originalName: encodedFilename,
          },
        });
        this.handleUploadCompleted(fileId);
      } catch (error) {}
    }
  }

  private handleChunkUploaded(
    fileId: string,
    blockId: string,
    onProgress: (progress: UploadProgress) => void,
  ) {
    const upload = this.uploadQueue.get(fileId);
    if (!upload) return;

    upload.completedChunks.add(blockId);
    upload.progress.uploaded++;
    onProgress(upload.progress);
  }

  private handleUploadCompleted(fileId: string) {
    const upload = this.uploadQueue.get(fileId);
    if (!upload) return;

    //upload.progress.status = 'completed';
    this.uploadQueue.delete(fileId);
  }

  /**
   * Upload a thumbnail image as a single chunk
   * @param thumbnailData - The thumbnail data (Blob or Base64 string)
   * @param writeUrl - The SAS URL for uploading the thumbnail
   * @returns Promise that resolves when upload is complete
   */
  async uploadThumbnail(
    thumbnailData: Blob | string,
    writeUrl: string,
  ): Promise<void> {
    try {
      // Create blob client with the SAS URL
      const blobClient = new BlockBlobClient(writeUrl);

      // Convert base64 to blob if needed
      const thumbnailBlob =
        typeof thumbnailData === 'string'
          ? this.base64ToBlob(thumbnailData, 'image/jpeg')
          : thumbnailData;

      // Upload the thumbnail in a single operation
      await blobClient.uploadData(thumbnailBlob);
    } catch (error) {}
  }

  /**
   * Upload a WebP version of an image
   * @param webpData - The WebP data (Blob or Base64 string)
   * @param writeUrl - The SAS URL for uploading the WebP
   * @returns Promise that resolves when upload is complete
   */
  async uploadWebp(webpData: Blob | string, writeUrl: string): Promise<void> {
    try {
      // Create blob client with the SAS URL
      const blobClient = new BlockBlobClient(writeUrl);

      // Convert base64 to blob if needed
      const webpBlob =
        typeof webpData === 'string'
          ? this.base64ToBlob(webpData, 'image/webp')
          : webpData;

      // Upload the WebP in a single operation
      await blobClient.uploadData(webpBlob);
    } catch (error) {
      console.error('Failed to upload WebP:', error);
    }
  }

  /**
   * Convert base64 string to Blob
   * @param base64 - The base64 string (with or without data URL prefix)
   * @param mimeType - The MIME type for the blob
   */
  private base64ToBlob(base64: string, mimeType: string = 'image/jpeg'): Blob {
    // Remove data URL prefix if present
    const base64Data = base64.includes('base64,')
      ? base64.split('base64,')[1]
      : base64;

    const byteString = atob(base64Data);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    return new Blob([arrayBuffer], { type: mimeType });
  }
}
